/**
 * A cota do Gemini no dia, compartilhada pelas telas da Central (prévia,
 * Assistente, perfil e Configuração): uma leitura serve a todas, e quem gasta
 * um pedido pede a releitura.
 *
 * `uso-ia` exige `central_projetos:configurar`; sem ela não se lê nada e os
 * avisos saem sem o "restam N". Resposta de ação que já traga `gemini_hoje`
 * entra pelo `absorver`, sem ir ao servidor.
 */
import { useCallback, useEffect, useState } from "react";

import { useAuth } from "../../context/AuthContext";
import { lerUsoIA } from "../../services/centralProjetos";
import { aoLimparDadosDoTenant } from "../../utils/tenantStorage";
import { PERM } from "./comum";

/** Leitura mais nova que isto serve a quem abrir outra tela. */
const VALIDADE_MS = 60 * 1000;

let guardado = null;
let lidoEm = 0;
let emVoo = null;
const ouvintes = new Set();

/** Guarda uma leitura que já veio de outro lugar (a Configuração lê o `uso-ia` inteiro). */
export function guardarGeminiHoje(gemini) {
  if (!gemini || typeof gemini !== "object") return;
  guardado = gemini;
  lidoEm = Date.now();
  ouvintes.forEach((avisar) => avisar(gemini));
}

aoLimparDadosDoTenant(() => {
  guardado = null;
  lidoEm = 0;
  emVoo = null;
});

function ler(request, { forcar }) {
  if (emVoo) return emVoo;
  if (!forcar && guardado && Date.now() - lidoEm < VALIDADE_MS) return Promise.resolve(guardado);
  emVoo = lerUsoIA(request)
    .then((resposta) => guardarGeminiHoje(resposta?.gemini_hoje))
    .finally(() => {
      emVoo = null;
    });
  return emVoo;
}

export default function useGeminiHoje() {
  const { request, hasPermission } = useAuth();
  const podeLer = Boolean(hasPermission?.(PERM.CONFIGURAR));
  const [gemini, setGemini] = useState(guardado);

  useEffect(() => {
    ouvintes.add(setGemini);
    return () => ouvintes.delete(setGemini);
  }, []);

  const carregar = useCallback(
    (forcar) => {
      if (!podeLer) return;
      ler(request, { forcar }).catch(() => {
        // Leitura de apoio: sem ela os avisos só perdem o "restam N"; o erro
        // de verdade aparece na ação que a pessoa fizer.
      });
    },
    [podeLer, request],
  );

  useEffect(() => {
    carregar(false);
  }, [carregar]);

  const recarregar = useCallback(() => carregar(true), [carregar]);
  return { gemini, recarregar, absorver: guardarGeminiHoje };
}
